import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Info as InfoIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Tooltip, Autocomplete, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import CustomButton from '../../../components/common/Button';
import { ShiftFormData } from '../types';
import { cityOptions } from '../../../constants/enums';
import { CREATE_SHIFT_SCHEDULE } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';

const CreateShift: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ShiftFormData>({
    name: '',
    startTime: '',
    endTime: '',
    requiredDrivers: 1,
    maxDrivers: 1,
    cityId: '',
    neighborhoodId: '',
    zone: '',
    startLatitude: undefined,
    startLongitude: undefined,
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [createShiftSchedule, { loading: saving }] = useMutation(CREATE_SHIFT_SCHEDULE, {
    ...stagingOptions,
    fetchPolicy: 'network-only',
  });

  const handleInputChange = (field: keyof ShiftFormData, value: string | number | undefined) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Shift Name is required';
    }

    if (!formData.startTime) {
      errors.startTime = 'Start Time is required';
    }

    if (!formData.endTime) {
      errors.endTime = 'End Time is required';
    }

    if (!formData.cityId) {
      errors.cityId = 'City is required';
    }

    const requiredDrivers = formData.requiredDrivers || formData.maxDrivers || 1;
    if (requiredDrivers < 1) {
      errors.requiredDrivers = 'Required Drivers must be at least 1';
    }

    if (formData.startTime) {
      const startDate = new Date(formData.startTime);
      if (isNaN(startDate.getTime())) {
        errors.startTime = 'Invalid Start Time format';
      }
    }

    if (formData.endTime) {
      const endDate = new Date(formData.endTime);
      if (isNaN(endDate.getTime())) {
        errors.endTime = 'Invalid End Time format';
      } else if (formData.startTime) {
        const startDate = new Date(formData.startTime);
        if (!isNaN(startDate.getTime()) && endDate <= startDate) {
          errors.endTime = 'End time must be after start time';
        }
      }
    }

    if (formData.startLatitude !== undefined) {
      if (formData.startLatitude < -90 || formData.startLatitude > 90) {
        errors.startLatitude = 'Start Latitude must be between -90 and 90';
      }
    }

    if (formData.startLongitude !== undefined) {
      if (formData.startLongitude < -180 || formData.startLongitude > 180) {
        errors.startLongitude = 'Start Longitude must be between -180 and 180';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setFieldErrors({});

    try {
      const input: {
        name: string;
        startTime: string;
        endTime: string;
        requiredDrivers: number;
        cityId: string;
        neighborhoodId?: string;
        startLatitude?: number;
        startLongitude?: number;
      } = {
        name: formData.name.trim(),
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        requiredDrivers: formData.requiredDrivers || formData.maxDrivers || 1,
        cityId: formData.cityId,
      };

      if (formData.neighborhoodId) {
        input.neighborhoodId = formData.neighborhoodId;
      }
      if (formData.startLatitude !== undefined) {
        input.startLatitude = formData.startLatitude;
      }
      if (formData.startLongitude !== undefined) {
        input.startLongitude = formData.startLongitude;
      }

      const response = await createShiftSchedule({
        variables: {
          input: input,
        },
      });

      if (response.data?.createShiftSchedule?.shift) {
        toast.success(`Shift created successfully (ID: ${response.data.createShiftSchedule.shift.id}, Status: ${response.data.createShiftSchedule.shift.status})`);
        navigate('/shift-management', { state: { refresh: true } });
      } else {
        toast.error('Failed to create shift. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to create shift', error);
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to create shift. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    navigate('/shift-management');
  };

  return (
    <div className="flex flex-col space-y-2 bg-white">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8">

          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '20px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="mb-8">
            <div className="bg-gradient-to-r from-primaryColor via-primaryColor to-darkBlue rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
              <div className="relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="text-3xl font-bold text-white">Create New Shift Session</h1>
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30">
                        New
                      </span>
                    </div>
                    <p className="text-base text-white/90 leading-relaxed max-w-2xl">
                      Create a new shift schedule to manage driver availability and assignments for specific time windows and locations. The shift will be created in draft status and can be activated later.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shift Session Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    placeholder="Enter shift session name"
                    className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {fieldErrors.name && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.startTime}
                      onChange={(e) => handleInputChange('startTime', e.target.value)}
                      required
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.startTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.startTime && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.startTime}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.endTime}
                      onChange={(e) => handleInputChange('endTime', e.target.value)}
                      required
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.endTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.endTime && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.endTime}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required Drivers <span className="text-red-500">*</span>
                      <Tooltip title="Maximum number of drivers allowed for this shift (default: 1). This determines the slot limit for driver assignments." arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={formData.requiredDrivers || formData.maxDrivers || 1}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        handleInputChange('requiredDrivers', value);
                        handleInputChange('maxDrivers', value);
                      }}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.requiredDrivers ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.requiredDrivers ? (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.requiredDrivers}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">Default: 1 driver</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <Autocomplete
                      options={cityOptions}
                      getOptionLabel={(option) => option.name || ''}
                      value={cityOptions.find(city => String(city.id) === String(formData.cityId)) || null}
                      onChange={(event, newValue) => {
                        const value = newValue ? String(newValue.id) : '';
                        handleInputChange('cityId', value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select a city"
                          required
                          error={!!fieldErrors.cityId}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '2px 4px',
                              fontSize: '14px',
                              '& fieldset': {
                                borderColor: fieldErrors.cityId ? '#F44336' : '#D1D5DB',
                              },
                              '&:hover fieldset': {
                                borderColor: fieldErrors.cityId ? '#F44336' : '#D1D5DB',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: fieldErrors.cityId ? '#F44336' : '#303F9F',
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              padding: '8px 12px',
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                          {option.name}
                        </li>
                      )}
                      sx={{
                        '& .MuiAutocomplete-inputRoot': {
                          padding: '0 !important',
                        },
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: '300px',
                        },
                      }}
                    />
                    {fieldErrors.cityId && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.cityId}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neighborhood / Working Zone
                    <Tooltip title="Optional: Select a specific city/neighborhood" arrow placement="top">
                      <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                    </Tooltip>
                  </label>
                  <Autocomplete
                    options={cityOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={cityOptions.find(city => String(city.id) === String(formData.neighborhoodId || formData.zone)) || null}
                    onChange={(event, newValue) => {
                      const value = newValue ? String(newValue.id) : '';
                      handleInputChange('neighborhoodId', value);
                      handleInputChange('zone', value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select a city/neighborhood (optional)"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            padding: '2px 4px',
                            fontSize: '14px',
                            '& fieldset': {
                              borderColor: '#D1D5DB',
                            },
                            '&:hover fieldset': {
                              borderColor: '#D1D5DB',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#303F9F',
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            padding: '8px 12px',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                        {option.name}
                      </li>
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        padding: '0 !important',
                      },
                    }}
                    ListboxProps={{
                      style: {
                        maxHeight: '300px',
                      },
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Latitude
                      <Tooltip title="Optional: Starting point latitude for geofencing and zone boundary alerts" arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="-90"
                      max="90"
                      value={formData.startLatitude || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        handleInputChange('startLatitude', value);
                      }}
                      placeholder="24.7136"
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.startLatitude ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.startLatitude && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.startLatitude}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Longitude
                      <Tooltip title="Optional: Starting point longitude for geofencing and zone boundary alerts" arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="-180"
                      max="180"
                      value={formData.startLongitude || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        handleInputChange('startLongitude', value);
                      }}
                      placeholder="46.6753"
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.startLongitude ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.startLongitude && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.startLongitude}</p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <InfoIcon sx={{ fontSize: '18px', color: '#3B82F6', marginTop: '2px' }} />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Note:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>The shift will be created in <strong>draft</strong> status</li>
                        <li>You can edit the shift later (only while in draft status)</li>
                        <li>Activate the shift manually or it will auto-activate when start_time is reached</li>
                        <li>Only draft or active shifts accept new driver assignments</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <CustomButton
                  variant="outlined"
                  color="primary"
                  onClick={handleCancel}
                  additionalStyles="min-w-[120px]"
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                  additionalStyles="min-w-[120px] shadow-md hover:shadow-lg transition-shadow"
                >
                  {saving ? 'Creating...' : 'Create Shift Session'}
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateShift;

